<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OrdersAPI from 'dashboard/api/orders';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import OrderForm from '../components/OrderForm.vue';

const { t } = useI18n();

const orders = ref([]);
const loading = ref(false);
const selectedOrder = ref(null);
const expandedOrderId = ref(null);
const showForm = ref(false);

// Header controls
const searchQuery = ref('');
const showFilterMenu = ref(false);
const showSortMenu = ref(false);
const showExportMenu = ref(false);
const filterStatus = ref(null);
const sortField = ref(null);
const sortOrder = ref('asc');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);
const showItemsPerPageMenu = ref(false);
const itemsPerPageOptions = [
  { value: 10, label: '10 / page' },
  { value: 25, label: '25 / page' },
  { value: 50, label: '50 / page' },
];

const headerTitle = computed(() => t('WEBSITE.HEADER.ORDERS_TITLE'));

// Filtrage et tri (sans pagination)
const filteredAndSortedOrders = computed(() => {
  let result = [...orders.value];

  // Recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      order =>
        order.order_number.toLowerCase().includes(query) ||
        order.customer_name.toLowerCase().includes(query) ||
        order.customer_email.toLowerCase().includes(query)
    );
  }

  // Filtre
  if (filterStatus.value) {
    result = result.filter(order => order.status === filterStatus.value);
  }

  // Tri
  if (sortField.value) {
    result.sort((a, b) => {
      let aVal = a[sortField.value];
      let bVal = b[sortField.value];

      if (sortField.value === 'total') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }

      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
});

// Pagination
const totalPages = computed(() =>
  Math.ceil(filteredAndSortedOrders.value.length / itemsPerPage.value)
);

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);

const endIndex = computed(() =>
  Math.min(
    startIndex.value + itemsPerPage.value,
    filteredAndSortedOrders.value.length
  )
);

// Résultats paginés (utilisé pour l'affichage)
const filteredOrders = computed(() => {
  return filteredAndSortedOrders.value.slice(startIndex.value, endIndex.value);
});

// Différencier état vide vs recherche sans résultat
const isEmpty = computed(() => !loading.value && orders.value.length === 0);

const hasNoSearchResults = computed(
  () =>
    !loading.value &&
    orders.value.length > 0 &&
    filteredAndSortedOrders.value.length === 0 &&
    (searchQuery.value || filterStatus.value)
);

const fetchOrders = async () => {
  loading.value = true;
  try {
    const response = await OrdersAPI.get();
    orders.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchOrders();
});

// Réinitialiser à la page 1 quand les filtres, la recherche ou itemsPerPage changent
watch([searchQuery, filterStatus, sortField, itemsPerPage], () => {
  currentPage.value = 1;
});

const openCreateForm = () => {
  selectedOrder.value = null;
  showForm.value = true;
};

const openEditForm = async order => {
  try {
    // Ne pas expand la ligne, juste ouvrir le formulaire
    const response = await OrdersAPI.show(order.id);
    selectedOrder.value = response.data;
    showForm.value = true;
    // Fermer l'expansion si elle était ouverte
    expandedOrderId.value = null;
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.FETCH_FAILED'));
  }
};

const closeForm = () => {
  showForm.value = false;
  selectedOrder.value = null;
};

const handleSave = async () => {
  await fetchOrders();
  closeForm();
};

const viewOrder = async order => {
  if (expandedOrderId.value === order.id) {
    expandedOrderId.value = null;
    selectedOrder.value = null;
    return;
  }

  try {
    const response = await OrdersAPI.show(order.id);
    selectedOrder.value = response.data;
    expandedOrderId.value = order.id;
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.FETCH_FAILED'));
  }
};

const formatPrice = price => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const formatOrderNumber = orderNumber => {
  if (!orderNumber) return '-';
  // Afficher seulement les 8 derniers caractères
  return orderNumber.slice(-8);
};

const getStatusColor = status => {
  const colors = {
    pending: {
      bg: 'bg-n-teal-2',
      text: 'text-n-teal-11',
      border: 'border-n-teal-6',
      dot: 'bg-n-teal-9',
    },
    processing: {
      bg: 'bg-n-blue-2',
      text: 'text-n-blue-11',
      border: 'border-n-blue-6',
      dot: 'bg-n-blue-9',
    },
    completed: {
      bg: 'bg-n-teal-2',
      text: 'text-n-teal-11',
      border: 'border-n-teal-6',
      dot: 'bg-n-teal-9',
    },
    cancelled: {
      bg: 'bg-n-ruby-2',
      text: 'text-n-ruby-11',
      border: 'border-n-ruby-6',
      dot: 'bg-n-ruby-9',
    },
  };
  return (
    colors[status] || {
      bg: 'bg-n-slate-2',
      text: 'text-n-slate-11',
      border: 'border-n-slate-6',
      dot: 'bg-n-slate-9',
    }
  );
};

const getStatusLabel = status => {
  const statusKey = status.toUpperCase();
  return t(`WEBSITE.ORDERS.STATUS.${statusKey}`);
};

const getPaymentMethodLabel = method => {
  if (!method) return '-';
  const methodKey = method.toUpperCase();
  const translationKey = `WEBSITE.ORDERS.PAYMENT_METHOD.${methodKey}`;
  const translated = t(translationKey);
  // Si la traduction retourne la clé brute, retourner une valeur par défaut
  if (translated === translationKey) {
    return methodKey === 'SAMPLE_REQUEST' ? 'none' : method;
  }
  return translated;
};

// Options pour les statuts
const statusOptions = computed(() => [
  { label: t('WEBSITE.ORDERS.STATUS.PENDING'), value: 'pending' },
  { label: t('WEBSITE.ORDERS.STATUS.PROCESSING'), value: 'processing' },
  { label: t('WEBSITE.ORDERS.STATUS.COMPLETED'), value: 'completed' },
  { label: t('WEBSITE.ORDERS.STATUS.CANCELLED'), value: 'cancelled' },
]);

const paymentStatusOptions = computed(() => [
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.PENDING'), value: 'pending' },
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.PAID'), value: 'paid' },
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.FAILED'), value: 'failed' },
  { label: t('WEBSITE.ORDERS.PAYMENT_STATUS.REFUNDED'), value: 'refunded' },
]);

const deliveryStatusOptions = computed(() => [
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.PENDING'), value: 'pending' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.PREPARING'), value: 'preparing' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.SHIPPED'), value: 'shipped' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.DELIVERED'), value: 'delivered' },
  { label: t('WEBSITE.ORDERS.DELIVERY_STATUS.CANCELLED'), value: 'cancelled' },
]);

// Mise à jour du statut de la commande
const updatingStatus = ref(false);
const updateOrderStatus = async newStatus => {
  if (!selectedOrder.value || updatingStatus.value) return;

  updatingStatus.value = true;
  try {
    await OrdersAPI.update(selectedOrder.value.id, {
      order: { status: newStatus },
    });
    // Rafraîchir les données
    const response = await OrdersAPI.show(selectedOrder.value.id);
    selectedOrder.value = response.data;
    // Mettre à jour aussi dans la liste
    const orderIndex = orders.value.findIndex(
      o => o.id === selectedOrder.value.id
    );
    if (orderIndex !== -1) {
      orders.value[orderIndex] = {
        ...orders.value[orderIndex],
        status: newStatus,
      };
    }
    useAlert(t('WEBSITE.ORDERS.API.SUCCESS.STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.UPDATE_FAILED'));
  } finally {
    updatingStatus.value = false;
  }
};

// Mise à jour du statut de paiement
const updatingPaymentStatus = ref(false);
const updatePaymentStatus = async newPaymentStatus => {
  if (!selectedOrder.value || updatingPaymentStatus.value) return;

  updatingPaymentStatus.value = true;
  try {
    await OrdersAPI.update(selectedOrder.value.id, {
      order: { payment_status: newPaymentStatus },
    });
    // Rafraîchir les données
    const response = await OrdersAPI.show(selectedOrder.value.id);
    selectedOrder.value = response.data;
    // Mettre à jour aussi dans la liste
    const orderIndex = orders.value.findIndex(
      o => o.id === selectedOrder.value.id
    );
    if (orderIndex !== -1) {
      orders.value[orderIndex] = {
        ...orders.value[orderIndex],
        payment_status: newPaymentStatus,
      };
    }
    useAlert(t('WEBSITE.ORDERS.API.SUCCESS.STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.UPDATE_FAILED'));
  } finally {
    updatingPaymentStatus.value = false;
  }
};

// Mise à jour du statut de livraison
const updatingDeliveryStatus = ref(false);
const updateDeliveryStatus = async newDeliveryStatus => {
  if (!selectedOrder.value || updatingDeliveryStatus.value) return;

  updatingDeliveryStatus.value = true;
  try {
    await OrdersAPI.update(selectedOrder.value.id, {
      order: { delivery_status: newDeliveryStatus },
    });
    // Rafraîchir les données
    const response = await OrdersAPI.show(selectedOrder.value.id);
    selectedOrder.value = response.data;
    // Mettre à jour aussi dans la liste
    const orderIndex = orders.value.findIndex(
      o => o.id === selectedOrder.value.id
    );
    if (orderIndex !== -1) {
      orders.value[orderIndex] = {
        ...orders.value[orderIndex],
        delivery_status: newDeliveryStatus,
      };
    }
    useAlert(t('WEBSITE.ORDERS.API.SUCCESS.STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('WEBSITE.ORDERS.API.ERROR.UPDATE_FAILED'));
  } finally {
    updatingDeliveryStatus.value = false;
  }
};

// Fermer tous les menus sauf celui spécifié
const closeAllMenusExcept = exceptMenu => {
  if (exceptMenu !== 'filter') showFilterMenu.value = false;
  if (exceptMenu !== 'sort') showSortMenu.value = false;
  if (exceptMenu !== 'export') showExportMenu.value = false;
  if (exceptMenu !== 'itemsPerPage') showItemsPerPageMenu.value = false;
};

// Gestion filtres, tri, export
const handleSort = field => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  showSortMenu.value = false;
};

const handleFilter = status => {
  filterStatus.value = status;
  showFilterMenu.value = false;
};

const exportToCSV = () => {
  const headers = [
    'Numéro',
    'Client',
    'Email',
    'Articles',
    'Total',
    'Statut',
    'Date',
  ];
  // Exporter tous les résultats filtrés, pas seulement la page actuelle
  const rows = filteredAndSortedOrders.value.map(order => [
    order.order_number,
    order.customer_name,
    order.customer_email,
    order.items_count,
    order.total,
    getStatusLabel(order.status),
    formatDate(order.created_at),
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `commandes-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  showExportMenu.value = false;
};

const exportToJSON = () => {
  // Exporter tous les résultats filtrés, pas seulement la page actuelle
  const jsonContent = JSON.stringify(filteredAndSortedOrders.value, null, 2);
  const blob = new Blob([jsonContent], {
    type: 'application/json;charset=utf-8;',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `commandes-${new Date().toISOString().split('T')[0]}.json`;
  link.click();
  showExportMenu.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  filterStatus.value = null;
  sortField.value = null;
  sortOrder.value = 'asc';
  currentPage.value = 1;
};

// Gestion pagination
const changePage = newPage => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    currentPage.value = newPage;
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const selectItemsPerPage = value => {
  itemsPerPage.value = value;
  showItemsPerPageMenu.value = false;
};

// Styles pour CSS Grid
const gridHeaderStyle = computed(() => ({
  display: 'grid',
  gridTemplateColumns:
    '32px minmax(90px, 0.8fr) minmax(160px, 1.2fr) 80px 100px 140px 100px 50px',
  columnGap: '0px',
}));

const gridRowStyle = computed(() => ({
  display: 'grid',
  gridTemplateColumns:
    '32px minmax(90px, 0.8fr) minmax(160px, 1.2fr) 80px 100px 140px 100px 50px',
  columnGap: '0px',
  alignItems: 'center',
}));

const gridDetailsStyle = computed(() => ({
  display: 'grid',
  gridTemplateColumns: '40px 1fr',
  columnGap: '0px',
}));

// Fonctions pour animation accordion
const onEnter = (el, done) => {
  el.style.height = '0';
  el.style.overflow = 'hidden';
  el.getBoundingClientRect(); // Force reflow
  el.style.transition = 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
  el.style.height = `${el.scrollHeight}px`;
  el.addEventListener('transitionend', done, { once: true });
};

const onAfterEnter = el => {
  el.style.height = 'auto';
  el.style.overflow = 'visible';
};

const onLeave = (el, done) => {
  el.style.height = `${el.scrollHeight}px`;
  el.style.overflow = 'hidden';
  el.getBoundingClientRect(); // Force reflow
  el.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 1, 1)';
  el.style.height = '0';
  el.addEventListener('transitionend', done, { once: true });
};

const onAfterLeave = el => {
  el.style.height = '';
  el.style.overflow = '';
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.ORDERS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-green-2 dark:bg-n-green-3 flex items-center justify-center"
      >
        <span class="i-lucide-shopping-cart size-10 text-n-green-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('WEBSITE.ORDERS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.ORDERS.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.ORDERS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <!-- Header bar avec search, filtres, tri, export -->
    <div
      v-else-if="!isEmpty"
      class="mb-3 bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm"
    >
      <div class="flex items-center gap-2 px-2.5 py-1.5">
        <!-- Search bar -->
        <div class="flex-1 max-w-sm">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('WEBSITE.ORDERS.LIST.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 !text-xs',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2 rtl:right-2 pointer-events-none"
              />
            </template>
          </Input>
        </div>

        <!-- Séparateur vertical -->
        <div class="h-6 w-px bg-n-weak/40" />

        <!-- Filtres, Tri, Export -->
        <div class="flex items-center gap-1.5">
          <!-- Filtre -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6': filterStatus,
              }"
              @click="
                closeAllMenusExcept('filter');
                showFilterMenu = !showFilterMenu;
              "
            >
              <span class="i-lucide-filter size-3.5" />
              <span>{{ t('WEBSITE.ORDERS.LIST.FILTER') }}</span>
              <span
                v-if="filterStatus"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showFilterMenu"
              v-on-clickaway="() => (showFilterMenu = false)"
              class="absolute right-0 mt-1.5 w-56 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{ 'bg-n-blue-2 text-n-blue-11': !filterStatus }"
                @click="handleFilter(null)"
              >
                <span class="font-medium">{{
                  t('WEBSITE.ORDERS.LIST.ALL_ORDERS')
                }}</span>
                <span
                  v-if="!filterStatus"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <div class="h-px bg-n-weak/30 my-2 mx-3" />
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-amber-2 text-n-amber-11': filterStatus === 'pending',
                }"
                @click="handleFilter('pending')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-amber-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.ORDERS.STATUS.PENDING') }}
                </span>
                <span
                  v-if="filterStatus === 'pending'"
                  class="i-lucide-check size-3.5 text-n-amber-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': filterStatus === 'processing',
                }"
                @click="handleFilter('processing')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-blue-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.ORDERS.STATUS.PROCESSING') }}
                </span>
                <span
                  v-if="filterStatus === 'processing'"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-teal-2 text-n-teal-11': filterStatus === 'completed',
                }"
                @click="handleFilter('completed')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-teal-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.ORDERS.STATUS.COMPLETED') }}
                </span>
                <span
                  v-if="filterStatus === 'completed'"
                  class="i-lucide-check size-3.5 text-n-teal-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-ruby-2 text-n-ruby-11': filterStatus === 'cancelled',
                }"
                @click="handleFilter('cancelled')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-ruby-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.ORDERS.STATUS.CANCELLED') }}
                </span>
                <span
                  v-if="filterStatus === 'cancelled'"
                  class="i-lucide-check size-3.5 text-n-ruby-11"
                />
              </button>
            </div>
          </div>

          <!-- Tri -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6': sortField,
              }"
              @click="
                closeAllMenusExcept('sort');
                showSortMenu = !showSortMenu;
              "
            >
              <span class="i-lucide-arrow-up-down size-3.5" />
              <span>{{ t('WEBSITE.ORDERS.LIST.SORT') }}</span>
              <span
                v-if="sortField"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showSortMenu"
              v-on-clickaway="() => (showSortMenu = false)"
              class="absolute right-0 mt-1.5 w-60 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'created_at',
                }"
                @click="handleSort('created_at')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-calendar size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.ORDERS.LIST.SORT_DATE') }}</span>
                </span>
                <span
                  v-if="sortField === 'created_at'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{ 'bg-n-blue-2 text-n-blue-11': sortField === 'total' }"
                @click="handleSort('total')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-euro size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.ORDERS.LIST.SORT_AMOUNT') }}</span>
                </span>
                <span
                  v-if="sortField === 'total'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'customer_name',
                }"
                @click="handleSort('customer_name')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-user size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.ORDERS.LIST.SORT_CLIENT') }}</span>
                </span>
                <span
                  v-if="sortField === 'customer_name'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
            </div>
          </div>

          <!-- Export -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-teal-2 hover:text-n-teal-11 hover:border-n-teal-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              @click="
                closeAllMenusExcept('export');
                showExportMenu = !showExportMenu;
              "
            >
              <span class="i-lucide-download size-3.5" />
              <span>{{ t('WEBSITE.ORDERS.LIST.EXPORT') }}</span>
            </button>

            <div
              v-if="showExportMenu"
              v-on-clickaway="() => (showExportMenu = false)"
              class="absolute right-0 mt-1.5 w-48 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center gap-2.5 group font-medium"
                @click="exportToCSV"
              >
                <span
                  class="i-lucide-file-spreadsheet size-3.5 text-n-teal-11 group-hover:scale-110 transition-transform"
                />
                <span>{{ t('WEBSITE.ORDERS.LIST.EXPORT_CSV') }}</span>
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center gap-2.5 group font-medium"
                @click="exportToJSON"
              >
                <span
                  class="i-lucide-file-code size-3.5 text-n-blue-11 group-hover:scale-110 transition-transform"
                />
                <span>{{ t('WEBSITE.ORDERS.LIST.EXPORT_JSON') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div
      v-if="hasNoSearchResults"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 border border-n-weak/70 rounded-lg"
    >
      <span class="i-lucide-search-x mb-4 text-n-slate-9 size-12" />
      <h3 class="text-base font-semibold text-center text-n-slate-12 mb-2">
        {{ t('WEBSITE.ORDERS.NO_RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.ORDERS.NO_RESULTS.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.ORDERS.NO_RESULTS.CLEAR_BUTTON')"
        icon="i-lucide-x"
        size="sm"
        variant="outline"
        color="slate"
        @click="clearSearch"
      />
    </div>

    <!-- Orders list -->
    <div
      v-else-if="!isEmpty && !hasNoSearchResults"
      class="bg-n-solid-1 border border-n-weak/70 overflow-hidden rounded-lg shadow-sm min-h-[400px]"
    >
      <div>
        <!-- Table Header -->
        <div
          class="px-3 py-2.5 text-xs font-semibold text-n-slate-11/80 bg-n-alpha-1/50 border-b border-n-weak/60"
          :style="gridHeaderStyle"
        >
          <div
            class="flex items-center justify-center border-r border-n-weak/20 pr-3"
          >
            <span class="i-lucide-chevron-right size-3.5 opacity-30" />
          </div>
          <div class="flex items-center gap-1.5 border-r border-n-weak/20 px-3">
            <span class="i-lucide-hash size-3.5 opacity-40" />
            <span>{{ t('WEBSITE.ORDERS.LIST.ORDER_NUMBER') }}</span>
          </div>
          <div class="flex items-center gap-1.5 border-r border-n-weak/20 px-3">
            <span class="i-lucide-user size-3.5 opacity-40" />
            <span>{{ t('WEBSITE.ORDERS.LIST.CUSTOMER') }}</span>
          </div>
          <div class="flex items-center gap-1.5 border-r border-n-weak/20 px-3">
            <span class="i-lucide-package size-3.5 opacity-40" />
            <span>{{ t('WEBSITE.ORDERS.LIST.ITEMS') }}</span>
          </div>
          <div class="flex items-center gap-1.5 border-r border-n-weak/20 px-3">
            <span class="i-lucide-euro size-3.5 opacity-40" />
            <span>{{ t('WEBSITE.ORDERS.LIST.TOTAL') }}</span>
          </div>
          <div class="flex items-center gap-1.5 border-r border-n-weak/20 px-3">
            <span class="i-lucide-activity size-3.5 opacity-40" />
            <span>{{ t('WEBSITE.ORDERS.LIST.STATUS') }}</span>
          </div>
          <div class="flex items-center gap-1.5 border-r border-n-weak/20 px-3">
            <span class="i-lucide-calendar size-3.5 opacity-40" />
            <span>{{ t('WEBSITE.ORDERS.LIST.DATE') }}</span>
          </div>
          <div class="flex items-center justify-center px-3">
            <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
          </div>
        </div>

        <!-- Table Body -->
        <template v-for="order in filteredOrders" :key="order.id">
          <!-- Order row -->
          <div
            class="px-3 py-2.5 group transition-all duration-150 ease-out border-b border-n-weak/40 cursor-pointer"
            :class="[
              expandedOrderId === order.id
                ? 'bg-n-blue-3/50'
                : 'bg-transparent hover:bg-n-blue-2/50',
            ]"
            :style="gridRowStyle"
            @click="viewOrder(order)"
          >
            <div
              class="flex items-center justify-center border-r border-n-weak/20 pr-3"
            >
              <div
                class="transition-transform duration-400 ease-out"
                :class="{ 'rotate-90': expandedOrderId === order.id }"
              >
                <span class="i-lucide-chevron-right size-4 text-n-slate-10" />
              </div>
            </div>

            <div
              class="flex items-center gap-2 min-w-0 border-r border-n-weak/20 px-2"
            >
              <div
                class="inline-flex items-center gap-1.5 px-2 py-1 bg-n-alpha-2/50 rounded-full border border-n-weak/40"
              >
                <span
                  class="i-lucide-receipt size-3 text-n-slate-11 flex-shrink-0"
                />
                <div class="min-w-0">
                  <div class="text-xs text-n-slate-12 truncate font-medium">
                    {{ `#${formatOrderNumber(order.order_number)}` }}
                  </div>
                </div>
              </div>
            </div>

            <div
              class="flex items-center min-w-0 border-r border-n-weak/20 px-3"
            >
              <div class="min-w-0">
                <div class="text-sm font-medium text-n-slate-12 truncate">
                  {{ order.customer_name }}
                </div>
                <div class="text-xs text-n-slate-10 truncate">
                  {{ order.customer_email }}
                </div>
              </div>
            </div>

            <div class="flex items-center border-r border-n-weak/20 px-2">
              <span class="text-xs text-n-slate-11">
                {{ order.items_count }}
                {{ t('WEBSITE.ORDERS.LIST.ITEMS_COUNT') }}
              </span>
            </div>

            <div class="flex items-center border-r border-n-weak/20 px-2">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ formatPrice(order.total) }}
              </span>
            </div>

            <div class="flex items-center border-r border-n-weak/20 px-2">
              <div
                class="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200"
                :class="[
                  getStatusColor(order.status).bg,
                  getStatusColor(order.status).text,
                  getStatusColor(order.status).border,
                ]"
              >
                <div
                  class="w-1.5 h-1.5 rounded-full animate-pulse"
                  :class="getStatusColor(order.status).dot"
                />
                {{ getStatusLabel(order.status) }}
              </div>
            </div>

            <div class="flex items-center border-r border-n-weak/20 px-2">
              <span class="text-xs text-n-slate-11 truncate">
                {{ formatDate(order.created_at) }}
              </span>
            </div>

            <div class="flex items-center justify-center px-2">
              <button
                class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                :title="t('WEBSITE.ORDERS.ACTIONS.EDIT')"
                @click.stop="openEditForm(order)"
              >
                <span class="i-lucide-edit size-4" />
              </button>
            </div>
          </div>

          <!-- Order details expandable -->
          <Transition
            name="accordion"
            @enter="onEnter"
            @after-enter="onAfterEnter"
            @leave="onLeave"
            @after-leave="onAfterLeave"
          >
            <div
              v-if="expandedOrderId === order.id && selectedOrder"
              class="bg-gradient-to-br from-n-alpha-2/60 to-n-alpha-1/40 border-b border-n-weak/50"
              :style="gridDetailsStyle"
            >
              <div class="border-r border-n-weak/20" />
              <div class="px-4 py-5">
                <!-- Grid layout pour les sections principales -->
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
                  <!-- Adresse de livraison -->
                  <div
                    class="lg:col-span-1 p-4 rounded-xl bg-n-solid-1 border border-n-weak/60 shadow-sm"
                  >
                    <div class="flex items-center gap-2 mb-3">
                      <div
                        class="w-8 h-8 rounded-lg bg-n-blue-2 flex items-center justify-center"
                      >
                        <span class="i-lucide-truck size-4 text-n-blue-11" />
                      </div>
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ t('WEBSITE.ORDERS.DETAILS.SHIPPING') }}
                      </h4>
                    </div>
                    <div
                      v-if="
                        selectedOrder.shipping_address &&
                        Object.keys(selectedOrder.shipping_address).length > 0
                      "
                      class="space-y-1.5 text-sm text-n-slate-11"
                    >
                      <p
                        v-if="selectedOrder.shipping_address.name"
                        class="font-medium text-n-slate-12 flex items-center gap-2"
                      >
                        <span class="i-lucide-user size-3.5 text-n-slate-10" />
                        {{ selectedOrder.shipping_address.name }}
                      </p>
                      <p
                        v-if="selectedOrder.shipping_address.street"
                        class="flex items-center gap-2"
                      >
                        <span
                          class="i-lucide-map-pin size-3.5 text-n-slate-10"
                        />
                        {{ selectedOrder.shipping_address.street }}
                      </p>
                      <p
                        v-if="
                          selectedOrder.shipping_address.postal_code ||
                          selectedOrder.shipping_address.city
                        "
                        class="flex items-center gap-2"
                      >
                        <span
                          class="i-lucide-building size-3.5 text-n-slate-10"
                        />
                        {{ selectedOrder.shipping_address.postal_code }}
                        {{ selectedOrder.shipping_address.city }}
                      </p>
                      <p
                        v-if="selectedOrder.shipping_address.country"
                        class="flex items-center gap-2"
                      >
                        <span class="i-lucide-globe size-3.5 text-n-slate-10" />
                        {{ selectedOrder.shipping_address.country }}
                      </p>
                    </div>
                    <div
                      v-else
                      class="flex items-center gap-2 text-sm text-n-slate-10 italic"
                    >
                      <span class="i-lucide-alert-circle size-4" />
                      {{ t('WEBSITE.ORDERS.DETAILS.NO_ADDRESS') }}
                    </div>

                    <!-- Delivery Status -->
                    <div class="mt-4 pt-4 border-t border-n-weak/60">
                      <div class="text-xs text-n-slate-10 mb-1.5">
                        {{ t('WEBSITE.ORDERS.FORM.DELIVERY_STATUS') }}
                      </div>
                      <div class="relative">
                        <div
                          class="relative inline-block w-full"
                          :class="{
                            'opacity-50 pointer-events-none':
                              updatingDeliveryStatus,
                          }"
                        >
                          <SelectMenu
                            :model-value="selectedOrder.delivery_status"
                            :options="deliveryStatusOptions"
                            @update:model-value="updateDeliveryStatus"
                          />
                        </div>
                        <div
                          v-if="updatingDeliveryStatus"
                          class="absolute inset-0 flex items-center justify-center z-10"
                        >
                          <Spinner :size="16" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Informations de paiement -->
                  <div
                    class="lg:col-span-1 p-4 rounded-xl bg-n-solid-1 border border-n-weak/60 shadow-sm"
                  >
                    <div class="flex items-center gap-2 mb-3">
                      <div
                        class="w-8 h-8 rounded-lg bg-n-teal-2 flex items-center justify-center"
                      >
                        <span
                          class="i-lucide-credit-card size-4 text-n-teal-11"
                        />
                      </div>
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ t('WEBSITE.ORDERS.DETAILS.PAYMENT') }}
                      </h4>
                    </div>
                    <div class="space-y-3">
                      <div>
                        <div class="text-xs text-n-slate-10 mb-1.5">
                          {{ t('WEBSITE.ORDERS.DETAILS.PAYMENT_METHOD') }}
                        </div>
                        <div
                          class="inline-flex items-center gap-2 px-2.5 py-1.5 bg-n-alpha-2 rounded-lg border border-n-weak/40"
                        >
                          <span
                            class="i-lucide-wallet size-3.5 text-n-slate-11"
                          />
                          <span class="text-sm font-medium text-n-slate-12">
                            {{
                              getPaymentMethodLabel(
                                selectedOrder.payment_method
                              )
                            }}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-n-slate-10 mb-1.5">
                          {{ t('WEBSITE.ORDERS.DETAILS.PAYMENT_STATUS') }}
                        </div>
                        <div class="relative">
                          <div
                            class="relative inline-block w-full"
                            :class="{
                              'opacity-50 pointer-events-none':
                                updatingPaymentStatus,
                            }"
                          >
                            <SelectMenu
                              :model-value="selectedOrder.payment_status"
                              :options="paymentStatusOptions"
                              @update:model-value="updatePaymentStatus"
                            />
                          </div>
                          <div
                            v-if="updatingPaymentStatus"
                            class="absolute inset-0 flex items-center justify-center z-10"
                          >
                            <Spinner :size="16" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-n-slate-10 mb-1.5">
                          {{ t('WEBSITE.ORDERS.LIST.STATUS') }}
                        </div>
                        <div class="relative">
                          <div
                            class="relative inline-block w-full"
                            :class="{
                              'opacity-50 pointer-events-none': updatingStatus,
                            }"
                          >
                            <SelectMenu
                              :model-value="selectedOrder.status"
                              :options="statusOptions"
                              @update:model-value="updateOrderStatus"
                            />
                          </div>
                          <div
                            v-if="updatingStatus"
                            class="absolute inset-0 flex items-center justify-center z-10"
                          >
                            <Spinner :size="16" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Résumé des montants -->
                  <div
                    class="lg:col-span-1 p-4 rounded-xl bg-gradient-to-br from-n-blue-2/30 to-n-teal-2/20 border border-n-blue-6/40 shadow-sm"
                  >
                    <div class="flex items-center gap-2 mb-3">
                      <div
                        class="w-8 h-8 rounded-lg bg-n-blue-3 flex items-center justify-center"
                      >
                        <span class="i-lucide-receipt size-4 text-n-blue-11" />
                      </div>
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ t('WEBSITE.ORDERS.DETAILS.TOTAL') }}
                      </h4>
                    </div>
                    <div class="space-y-2.5">
                      <div class="flex justify-between items-center text-sm">
                        <span class="text-n-slate-11">
                          {{ t('WEBSITE.ORDERS.DETAILS.SUBTOTAL') }}
                        </span>
                        <span class="font-medium text-n-slate-12">
                          {{ formatPrice(selectedOrder.subtotal) }}
                        </span>
                      </div>
                      <div
                        v-if="selectedOrder.tax > 0"
                        class="flex justify-between items-center text-sm"
                      >
                        <span class="text-n-slate-11">
                          {{ t('WEBSITE.ORDERS.DETAILS.TAX') }}
                        </span>
                        <span class="font-medium text-n-slate-12">
                          {{ formatPrice(selectedOrder.tax) }}
                        </span>
                      </div>
                      <div
                        v-if="selectedOrder.shipping_cost > 0"
                        class="flex justify-between items-center text-sm"
                      >
                        <span class="text-n-slate-11">
                          {{ t('WEBSITE.ORDERS.DETAILS.SHIPPING_COST') }}
                        </span>
                        <span class="font-medium text-n-slate-12">
                          {{ formatPrice(selectedOrder.shipping_cost) }}
                        </span>
                      </div>
                      <div
                        v-if="selectedOrder.discount > 0"
                        class="flex justify-between items-center text-sm"
                      >
                        <span class="text-n-slate-11">
                          {{ t('WEBSITE.ORDERS.DETAILS.DISCOUNT') }}
                        </span>
                        <span class="font-medium text-n-green-11">
                          -{{ formatPrice(selectedOrder.discount) }}
                        </span>
                      </div>
                      <div
                        class="flex justify-between items-center pt-2.5 border-t border-n-weak/60"
                      >
                        <span class="text-base font-semibold text-n-slate-12">
                          {{ t('WEBSITE.ORDERS.DETAILS.TOTAL') }}
                        </span>
                        <span class="text-lg font-bold text-n-blue-12">
                          {{ formatPrice(selectedOrder.total) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Section des articles -->
                <div class="mb-5">
                  <div class="flex items-center gap-2 mb-3">
                    <div
                      class="w-8 h-8 rounded-lg bg-n-purple-2 flex items-center justify-center"
                    >
                      <span
                        class="i-lucide-shopping-bag size-4 text-n-purple-11"
                      />
                    </div>
                    <h4 class="text-sm font-semibold text-n-slate-12">
                      {{ t('WEBSITE.ORDERS.DETAILS.ITEMS') }}
                    </h4>
                    <span
                      class="px-2 py-0.5 text-xs font-medium bg-n-alpha-2 rounded-full text-n-slate-11 border border-n-weak/40"
                    >
                      {{ selectedOrder.items?.length || 0 }}
                    </span>
                  </div>
                  <div class="space-y-2.5">
                    <div
                      v-for="item in selectedOrder.items"
                      :key="item.id"
                      class="flex items-center justify-between p-4 rounded-xl border border-n-weak/60 bg-n-solid-1 hover:bg-n-alpha-1/50 transition-all duration-200 shadow-sm"
                    >
                      <div class="flex items-center gap-4 flex-1 min-w-0">
                        <div
                          class="w-14 h-14 rounded-xl bg-gradient-to-br from-n-purple-2 to-n-blue-2 flex items-center justify-center flex-shrink-0 border border-n-weak/40"
                        >
                          <span
                            class="i-lucide-package size-6 text-n-purple-11"
                          />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div
                            class="text-sm font-semibold text-n-slate-12 mb-1 truncate"
                          >
                            {{ item.product_name }}
                          </div>
                          <div
                            class="flex flex-wrap items-center gap-2 text-xs text-n-slate-10"
                          >
                            <span
                              v-if="item.product_sku"
                              class="inline-flex items-center gap-1 px-2 py-0.5 bg-n-alpha-2 rounded border border-n-weak/40"
                            >
                              <span class="i-lucide-barcode size-3" />
                              {{ item.product_sku }}
                            </span>
                            <span class="inline-flex items-center gap-1">
                              <span class="i-lucide-hash size-3" />
                              {{ t('WEBSITE.ORDERS.DETAILS.QUANTITY') }}:
                              <span class="font-medium text-n-slate-11">
                                {{ item.quantity }}
                              </span>
                            </span>
                            <span class="inline-flex items-center gap-1">
                              <span class="i-lucide-euro size-3" />
                              {{ formatPrice(item.price) }}
                              <span class="text-n-slate-9 mx-1">/</span>
                              <span class="text-n-slate-10">
                                {{ t('WEBSITE.ORDERS.DETAILS.UNIT') }}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        class="ml-4 text-base font-bold text-n-slate-12 flex-shrink-0"
                      >
                        {{ formatPrice(item.total) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Notes -->
                <div class="space-y-3">
                  <div
                    v-if="selectedOrder.customer_note"
                    class="p-4 rounded-xl bg-n-blue-2/50 border border-n-blue-6/60 shadow-sm"
                  >
                    <div class="flex items-center gap-2 mb-2">
                      <div
                        class="w-6 h-6 rounded-lg bg-n-blue-3 flex items-center justify-center"
                      >
                        <span
                          class="i-lucide-message-square size-3.5 text-n-blue-11"
                        />
                      </div>
                      <h4 class="text-sm font-semibold text-n-blue-12">
                        {{ t('WEBSITE.ORDERS.DETAILS.CUSTOMER_NOTE') }}
                      </h4>
                    </div>
                    <p class="text-sm text-n-blue-11 leading-relaxed">
                      {{ selectedOrder.customer_note }}
                    </p>
                  </div>

                  <div
                    v-if="selectedOrder.internal_note"
                    class="p-4 rounded-xl bg-n-orange-2/50 border border-n-orange-6/60 shadow-sm"
                  >
                    <div class="flex items-center gap-2 mb-2">
                      <div
                        class="w-6 h-6 rounded-lg bg-n-orange-3 flex items-center justify-center"
                      >
                        <span
                          class="i-lucide-sticky-note size-3.5 text-n-orange-11"
                        />
                      </div>
                      <h4 class="text-sm font-semibold text-n-orange-12">
                        {{ t('WEBSITE.ORDERS.DETAILS.INTERNAL_NOTE') }}
                      </h4>
                    </div>
                    <p class="text-sm text-n-orange-11 leading-relaxed">
                      {{ selectedOrder.internal_note }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </template>
      </div>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="
        !isEmpty && !hasNoSearchResults && filteredAndSortedOrders.length > 0
      "
      class="mt-6"
    >
      <div
        class="flex justify-between items-center h-14 w-full bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm px-4 py-2"
      >
        <!-- Left: Items per page selector -->
        <div class="flex items-center gap-3">
          <span class="text-xs text-n-slate-11 font-medium whitespace-nowrap">
            {{ t('WEBSITE.ORDERS.PAGINATION.ITEMS_PER_PAGE') }}
          </span>
          <div class="relative">
            <button
              class="flex items-center gap-2 h-8 px-3 pr-2.5 text-xs font-medium bg-n-alpha-2 border border-n-weak/60 rounded-lg text-n-slate-12 hover:bg-n-alpha-3 hover:border-n-weak transition-all"
              :class="{
                'bg-n-blue-2 border-n-blue-6': showItemsPerPageMenu,
              }"
              @click="
                closeAllMenusExcept('itemsPerPage');
                showItemsPerPageMenu = !showItemsPerPageMenu;
              "
            >
              <span class="i-lucide-rows-3 size-3.5 text-n-slate-11" />
              <span class="tabular-nums">{{ itemsPerPage }}</span>
              <span
                class="i-lucide-chevron-down size-3 text-n-slate-11 transition-transform duration-200"
                :class="{ 'rotate-180': showItemsPerPageMenu }"
              />
            </button>

            <!-- Dropdown menu -->
            <div
              v-if="showItemsPerPageMenu"
              v-on-clickaway="() => (showItemsPerPageMenu = false)"
              class="absolute left-0 bottom-full mb-2 w-36 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                v-for="option in itemsPerPageOptions"
                :key="option.value"
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': itemsPerPage === option.value,
                }"
                @click="selectItemsPerPage(option.value)"
              >
                <span class="font-medium flex items-center gap-2">
                  <span
                    class="i-lucide-list size-3.5 group-hover:scale-110 transition-transform"
                  />
                  {{ option.label }}
                </span>
                <span
                  v-if="itemsPerPage === option.value"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
            </div>
          </div>
          <span
            class="text-xs text-n-slate-11 hidden sm:block whitespace-nowrap"
          >
            {{
              t('WEBSITE.ORDERS.PAGINATION.SHOWING', {
                start: startIndex + 1,
                end: endIndex,
                total: filteredAndSortedOrders.length,
              })
            }}
          </span>
        </div>

        <!-- Right: Pagination controls -->
        <div class="flex items-center gap-2">
          <Button
            icon="i-lucide-chevrons-left"
            variant="ghost"
            size="sm"
            color="slate"
            class="!w-8 !h-7"
            :disabled="currentPage === 1"
            @click="changePage(1)"
          />
          <Button
            icon="i-lucide-chevron-left"
            variant="ghost"
            color="slate"
            size="sm"
            class="!w-8 !h-7"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          />
          <div class="inline-flex items-center gap-2 text-xs text-n-slate-11">
            <span
              class="px-3 py-1 bg-n-alpha-2 rounded-md font-medium tabular-nums"
            >
              {{ currentPage }}
            </span>
            <span class="hidden sm:inline">
              {{ t('WEBSITE.ORDERS.PAGINATION.OF') }}
            </span>
            <span class="font-medium tabular-nums">{{ totalPages }}</span>
          </div>
          <Button
            icon="i-lucide-chevron-right"
            variant="ghost"
            color="slate"
            size="sm"
            class="!w-8 !h-7"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          />
          <Button
            icon="i-lucide-chevrons-right"
            variant="ghost"
            color="slate"
            size="sm"
            class="!w-8 !h-7"
            :disabled="currentPage === totalPages"
            @click="changePage(totalPages)"
          />
        </div>
      </div>
    </div>

    <!-- Order Form -->
    <OrderForm
      v-if="showForm"
      :order="selectedOrder"
      @close="closeForm"
      @save="handleSave"
    />
  </WebsiteLayout>
</template>
